import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { adsSummary, fmtMoney, fmtNum, fmtPct } from "@/lib/api";
import { GoogleAdsConnect } from "@/components/GoogleAdsConnect";
import { MetaAdsConnect } from "@/components/MetaAdsConnect";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { StatCard } from "@/components/common/StatCard";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { CurrencyDollar, Target, ShoppingBag, CursorClick } from "@phosphor-icons/react";

const PRESETS = [{ l: "7d", d: 7 }, { l: "28d", d: 28 }, { l: "90d", d: 90 }];
const rangeFor = (days) => {
  const e = new Date(), s = new Date(); s.setDate(e.getDate() - (days - 1));
  const f = (d) => d.toISOString().slice(0, 10);
  return { start: f(s), end: f(e) };
};

export default function AdsPage() {
  const { activeId } = useClient();
  const [days, setDays] = useState(28);
  const [platform, setPlatform] = useState("all");
  const [campaign, setCampaign] = useState("all");
  const { start, end } = rangeFor(days);

  const q = useQuery({ queryKey: ["ads", activeId, platform, campaign, days], queryFn: () => adsSummary(activeId, { platform, campaign, start, end }), enabled: !!activeId });
  const d = q.data;
  const t = d?.totals || {};

  return (
    <PageContainer>
      <PageHeader eyebrow="Performance" title="Meta & Google Ads"
        subtitle="Read-only ad performance across Meta and Google. Filter by platform, campaign and date. Nothing edits your campaigns — analytics only.">
        <div className="flex items-center gap-2 flex-wrap">
          <MetaAdsConnect clientId={activeId} />
          <GoogleAdsConnect clientId={activeId} />
        </div>
      </PageHeader>

      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded-lg bg-white/5 p-1">
            {[["all", "All"], ["meta", "Meta"], ["google", "Google"]].map(([k, l]) => (
              <button key={k} data-testid={`ads-platform-${k}`} onClick={() => { setPlatform(k); setCampaign("all"); }} className={`px-3 py-1.5 rounded-md text-sm ${platform === k ? "bg-[#6B3DFF] text-white" : "text-[#9a9a9a]"}`}>{l}</button>
            ))}
          </div>
          <select data-testid="ads-campaign-select" value={campaign} onChange={(e) => setCampaign(e.target.value)} className="bg-[#10101A] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#cfcfcf]">
            {(d?.campaign_names || ["all"]).map((c) => <option key={c} value={c}>{c === "all" ? "All campaigns" : c}</option>)}
          </select>
        </div>
        <div className="flex rounded-lg bg-white/5 p-1">
          {PRESETS.map((p) => (
            <button key={p.l} data-testid={`ads-range-${p.l}`} onClick={() => setDays(p.d)} className={`px-3 py-1.5 rounded-md text-sm ${days === p.d ? "bg-[#6B3DFF] text-white" : "text-[#9a9a9a]"}`}>{p.l}</button>
          ))}
        </div>
      </div>
      <span data-testid="ads-data-source" className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border mb-4 ${d?.data_source === "live" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/25" : "bg-white/5 text-[#9a9a9a] border-white/10"}`}>
        <span className={`h-1.5 w-1.5 rounded-full ${d?.data_source === "live" ? "bg-emerald-400" : "bg-[#7a7a7a]"}`} />
        {d?.data_source === "live" ? "Live ad data" : "No ad platforms connected — connect Meta/Google to see live data"}
      </span>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Ad Spend" value={fmtMoney(t.spend)} delta={d?.deltas?.spend} icon={CurrencyDollar} accent />
        <StatCard label="Revenue" value={fmtMoney(t.revenue)} delta={d?.deltas?.revenue} icon={CurrencyDollar} />
        <StatCard label="ROAS" value={`${t.roas || 0}x`} delta={d?.deltas?.roas} icon={Target} accent />
        <StatCard label="Purchases" value={fmtNum(t.purchases)} delta={d?.deltas?.purchases} icon={ShoppingBag} />
        <StatCard label="CPC" value={fmtMoney(t.cpc)} icon={CursorClick} />
        <StatCard label="CPM" value={fmtMoney(t.cpm)} icon={CursorClick} />
        <StatCard label="CTR" value={fmtPct(t.ctr)} icon={Target} />
        <StatCard label="Cost / Purchase" value={fmtMoney(t.cost_per_purchase)} icon={CurrencyDollar} accent />
      </div>

      <div className="card-matte p-5 mb-6" data-testid="ads-spend-chart">
        <div className="label-caps mb-3">Spend vs Revenue</div>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={d?.series || []}>
            <defs>
              <linearGradient id="adRev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2F9E44" stopOpacity={0.4} /><stop offset="100%" stopColor="#2F9E44" stopOpacity={0} /></linearGradient>
              <linearGradient id="adSpend" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#6B3DFF" stopOpacity={0.4} /><stop offset="100%" stopColor="#6B3DFF" stopOpacity={0} /></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
            <XAxis dataKey="date" tick={{ fill: "#7a7a7a", fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
            <YAxis tick={{ fill: "#7a7a7a", fontSize: 11 }} />
            <Tooltip contentStyle={{ background: "#14141F", border: "1px solid #ffffff20", borderRadius: 8 }} />
            <Area type="monotone" dataKey="revenue" stroke="#2F9E44" strokeWidth={2.5} fill="url(#adRev)" />
            <Area type="monotone" dataKey="spend" stroke="#6B3DFF" strokeWidth={2.5} fill="url(#adSpend)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="card-matte overflow-hidden" data-testid="ads-campaign-table">
        <div className="label-caps p-4 pb-0">Campaign Performance</div>
        <div className="overflow-x-auto p-4">
          <Table>
            <TableHeader><TableRow className="border-white/10 hover:bg-transparent">{["Campaign", "Platform", "Spend", "Impr.", "Clicks", "CTR", "CPC", "Purch.", "Revenue", "ROAS"].map((h) => <TableHead key={h} className="label-caps text-[#7a7a7a]">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>{(d?.campaigns || []).map((c, i) => (
              <TableRow key={i} className="border-white/8 hover:bg-white/[0.03]">
                <TableCell className="text-[#cfcfcf]">{c.name}</TableCell>
                <TableCell><span className={`text-[10px] uppercase px-1.5 py-0.5 rounded ${c.platform === "meta" ? "bg-sky-500/15 text-sky-400" : "bg-amber-500/15 text-amber-400"}`}>{c.platform}</span></TableCell>
                <TableCell>{fmtMoney(c.spend)}</TableCell><TableCell>{fmtNum(c.impressions)}</TableCell><TableCell>{fmtNum(c.clicks)}</TableCell>
                <TableCell>{fmtPct(c.ctr)}</TableCell><TableCell>{fmtMoney(c.cpc)}</TableCell><TableCell>{fmtNum(c.purchases)}</TableCell>
                <TableCell>{fmtMoney(c.revenue)}</TableCell><TableCell className={c.roas >= 3 ? "text-emerald-400 font-semibold" : "brand-text"}>{c.roas}x</TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        </div>
      </div>
    </PageContainer>
  );
}
